/**
 * THE STORAGE HALF OF THE BUILD-TIME FIREBASE CONFIG - two variables, read here and nowhere else.
 *
 * Two readers need them and must not import each other. ./firebase-quarantine-uploader.ts needs
 * both, and it carries `firebase/storage`, which is why ./create-quarantine-uploader.ts only ever
 * loads it lazily; ./emulator-image-url.ts needs the emulator alone, and is meant to run in every
 * build. So the reading lives in this file, which imports nothing at all.
 *
 * READ AT CALL TIME, one dotted name at a time: the form Vite inlines at build, the form the
 * sign-in half reads its own three values in (`readFirebaseConfig`,
 * ../auth/firebase/firebase-auth-client.ts), and the form a spec's `vi.stubEnv` reaches. Both
 * variables are declared, with what each is for, in src/vite-env.d.ts, and `dev:stack`
 * (web/authors/package.json) sets both.
 */

/** The Storage emulator's address, in the two shapes its two readers want it in. */
export interface StorageEmulator {
  /** `http://127.0.0.1:9199` - what a production URL's origin is swapped for. */
  readonly origin: string;
  /** `127.0.0.1` - `connectStorageEmulator` takes the host and the port apart. */
  readonly host: string;
  /** `9199`. */
  readonly port: number;
  /**
   * ★ THE TUNNEL MODE ON AN https PAGE (`self`, `dev:tunnel` behind a quick tunnel): the emulator is
   * the page's own origin, which the dev server forwards to it (vite.config.ts). The SDK's emulator
   * connection speaks plain http only, so the uploader points the instance there itself.
   */
  readonly https: boolean;
}

function present(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function parsed(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * The bucket every upload is written into, as a bare name: `hoolimoon.appspot.com`, the platform's
 * ONE bucket (docs/design/04-schema.md).
 *
 * ★ THAT BUCKET EVEN AGAINST THE EMULATOR, never the demo project's `<project>.appspot.com`, which
 * is the natural guess and the wrong one. The claim reads the quarantine object back out of the
 * catalogue's default bucket BY NAME (`defaultBucket()`,
 * src/modules/authors/services/avatar-claim.service.ts, then :218-219), so an object written
 * into any other bucket is a claim answered `UPLOAD_OBJECT_NOT_FOUND`, every time.
 *
 * REQUIRED, and a missing value refuses rather than guesses - the rule the sign-in half keeps for
 * its own three values. It is read at the first write, so a live build without it still signs in
 * and fills a form; it cannot upload, and the error says which variable to set.
 */
export function readStorageBucket(): string {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  if (!present(bucket)) {
    throw new Error(
      'The live uploader writes to Firebase Storage, but the build has no value for ' +
        'VITE_FIREBASE_STORAGE_BUCKET. web/authors/.env.example says what it is, and the ' +
        'dev:stack script in web/authors/package.json sets it.',
    );
  }
  return bucket;
}

/**
 * The Storage emulator, or `null` when the build names none - which is the production build. Only
 * `dev:stack` names one, the emulator the dev compose file publishes on 9199, and `dev:tunnel`, whose
 * `self` is the page's own origin (`storageEmulatorFrom` below).
 *
 * A VALUE THAT CANNOT BE SPLIT INTO AN http ORIGIN WITH A PORT THROWS, naming the variable. Reading
 * a typo as "no emulator" would send a laptop's uploads to the real service with a demo project's
 * token; and `https` is refused because the SDK would speak plain http to it anyway.
 */
export function readStorageEmulator(): StorageEmulator | null {
  return storageEmulatorFrom(
    import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_URL,
    typeof window === 'undefined' ? null : window.location.origin,
  );
}

/**
 * `readStorageEmulator` with the page named, so a spec can name an https one.
 *
 * ★ `self` (the tunnel mode, `dev:tunnel`) IS THE PAGE'S OWN ORIGIN, which the dev server forwards
 * to the emulator (vite.config.ts) - a teammate's `127.0.0.1` is their own machine. The one https
 * value there is: the page's, never one written in a variable.
 */
export function storageEmulatorFrom(
  configured: string | undefined,
  pageOrigin: string | null,
): StorageEmulator | null {
  if (!present(configured)) {
    return null;
  }
  if (configured === 'self') {
    const page = pageOrigin === null ? null : parsed(pageOrigin);
    if (page === null || (page.protocol !== 'http:' && page.protocol !== 'https:')) {
      throw new Error(
        'VITE_FIREBASE_STORAGE_EMULATOR_URL is "self", the page\'s own origin, but there is no ' +
          'http or https page to take it from.',
      );
    }
    const https = page.protocol === 'https:';
    const port = page.port.length > 0 ? Number(page.port) : https ? 443 : 80;
    return { origin: page.origin, host: page.hostname, port, https };
  }
  const url = parsed(configured);
  if (url === null || url.protocol !== 'http:' || url.port.length === 0) {
    throw new Error(
      `VITE_FIREBASE_STORAGE_EMULATOR_URL is "${configured}", which is not an http origin with a ` +
        'port. Against the local stack it is http://127.0.0.1:9199 (web/authors/.env.example).',
    );
  }
  return { origin: url.origin, host: url.hostname, port: Number(url.port), https: false };
}
